import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAuth } from '@/contexts/AuthContext'
import { AppHeader } from '@/components/ui/AppHeader'

interface HomePageProps {
    onNavigate: (page: string) => void
    onShowConfirm: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const HomePage = ({
    onNavigate,
    onShowConfirm,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm,
}: HomePageProps) => {
    const { user, userProfile, profileLoading, signOut } = useAuth()

    if (profileLoading) {
        return (
            <div className="flex justify-center pt-24">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement du profil...</p>
                </div>
            </div>
        )
    }

    if (!userProfile) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-8">
                <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Profil non trouvé</h2>
                    <p className="text-gray-600 mb-6">
                        Aucun profil n'est associé à votre compte. Veuillez contacter un administrateur pour résoudre ce problème. Vous pouvez aussi vous déconnecter et vous reconnecter ou vérifier votre connexion internet.
                    </p>
                    <Button variant="outline" onClick={signOut}>
                        Retour à la connexion
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                            <img src="/favicon.png" alt="Housing First" className="h-8 w-8 object-contain" />
                            <span className="text-lg sm:text-xl text-gray-900">
                                <span className="font-normal">Hello, </span>
                                <span className="font-bold">{user?.user_metadata?.full_name.split(' ')[0]}</span>
                            </span>
                        </div>
                        <AppHeader />
                    </div>
                </div>
            </header>
            <main className="max-w-2xl mx-auto pt-12 pb-20 px-4">
                <div className="text-center mb-12">
                    <div className="mx-auto flex items-center justify-center mb-2">
                        <img
                            src="/logo_doucheflux.jpeg"
                            alt="DoucheFLUX Housing First"
                            className="h-48 object-contain"
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    <Button
                        onClick={() => onNavigate('nouvelle_entree')}
                        className="w-full py-4 sm:py-6 text-sm sm:text-lg"
                        size="lg"
                    >
                        Nouvelle entrée
                    </Button>

                    <Button
                        onClick={() => onNavigate('consultation_notes_stats')}
                        variant="outline"
                        className="w-full py-4 sm:py-6 text-sm sm:text-lg"
                        size="lg"
                    >
                        Consultation de notes et stats
                    </Button>
                    <Button
                        onClick={() => onNavigate('beneficiaires')}
                        variant="outline"
                        className="w-full py-4 sm:py-6 text-sm sm:text-lg"
                        size="lg"
                    >
                        Locataires
                    </Button>

                    <Button
                        onClick={() => onNavigate('data_analysis')}
                        variant="outline"
                        className="w-full py-4 sm:py-6 text-sm sm:text-lg"
                        size="lg"
                    >
                        Analyse de données
                    </Button>
                </div>

            </main>

            <footer className="fixed bottom-0 left-0 right-0 flex flex-col items-center gap-0.5 py-2 text-xs text-gray-400">
                <a href="https://hypha.be" target="_blank" rel="noreferrer" className="transition-opacity hover:opacity-80">
                    <img
                        src="https://www.hypha.be/developpe-par-hypha.png"
                        alt="Développé par Hypha.be avec cœur"
                        className="h-7 w-auto"
                    />
                </a>
                <p>Version 2.3</p>
            </footer>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
}
